import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, Divider, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { XIcon, InfoIcon } from 'lucide-react';
interface PartnerUserFormProps {
  open: boolean;
  user: any | null;
  onClose: () => void;
  onSave: (userData: any) => void;
  preselectedPartner: string | null;
}
interface FormData {
  name: string;
  fatherName: string;
  idNumber: string;
  email: string;
  phone: string;
  username: string;
  partner: string;
  userType: 'maker' | 'checker';
}
interface FormErrors {
  name?: string;
  fatherName?: string;
  idNumber?: string;
  email?: string;
  phone?: string;
  username?: string;
  partner?: string;
  userType?: string;
}
const PARTNERS = ['GTBank Ghana', 'First Bank Nigeria', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal'];
const PartnerUserFormDrawer: React.FC<PartnerUserFormProps> = ({
  open,
  user,
  onClose,
  onSave,
  preselectedPartner
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    fatherName: '',
    idNumber: '',
    email: '',
    phone: '',
    username: '',
    partner: '',
    userType: 'maker'
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        fatherName: user.fatherName || '',
        idNumber: user.idNumber || '',
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || '',
        partner: user.partner || '',
        userType: user.userType || 'maker'
      });
    } else {
      setFormData({
        name: '',
        fatherName: '',
        idNumber: '',
        email: '',
        phone: '',
        username: '',
        partner: preselectedPartner || '',
        userType: 'maker'
      });
    }
    setErrors({});
  }, [user, open, preselectedPartner]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+\d{7,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'International format required, e.g. +233 20 123 4567';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-z0-9.]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain lowercase letters, numbers, and dots';
    }
    if (!formData.partner) {
      newErrors.partner = 'Partner is required';
    }
    if (!formData.userType) {
      newErrors.userType = 'User Type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: user?.id
      });
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-1310">
      <Box sx={{
      p: 3
    }} data-id="element-1311">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-1312">
          <Typography variant="h6" data-id="element-1313">
            {user ? 'Edit User' : 'Add New User'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-1314">
            <XIcon size={18} data-id="element-1315" />
          </IconButton>
        </Box>
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-1316">
          <TextField fullWidth margin="normal" label="Name" name="name" value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} required data-id="element-1317" />
          <TextField fullWidth margin="normal" label="Department" name="fatherName" value={formData.fatherName} onChange={handleChange} error={!!errors.fatherName} helperText={errors.fatherName} data-id="element-1318" />
          <TextField fullWidth margin="normal" label="Passport/ID (Optional)" name="idNumber" value={formData.idNumber} onChange={handleChange} error={!!errors.idNumber} helperText={errors.idNumber} data-id="element-1319" />
          <TextField fullWidth margin="normal" label="Email" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required data-id="element-1320" />
          <TextField fullWidth margin="normal" label="Phone" name="phone" value={formData.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone || 'International format, e.g. +233 20 123 4567'} required data-id="element-1321" />
          <TextField fullWidth margin="normal" label="Username" name="username" value={formData.username} onChange={handleChange} error={!!errors.username} helperText={errors.username || 'Lowercase letters, numbers, and dots only'} required data-id="element-1322" />
          <FormControl fullWidth margin="normal" error={!!errors.partner} required data-id="element-1323">
            <InputLabel id="partner-label" data-id="element-1324">Partner</InputLabel>
            <Select labelId="partner-label" name="partner" value={formData.partner} onChange={handleChange} label="Partner" data-id="element-1325">
              {PARTNERS.map(partner => <MenuItem key={partner} value={partner} data-id="element-1326">
                  {partner}
                </MenuItem>)}
            </Select>
            {errors.partner && <FormHelperText data-id="element-1327">{errors.partner}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth margin="normal" error={!!errors.userType} required data-id="element-1328">
            <InputLabel id="user-type-label" data-id="element-1329">User Type</InputLabel>
            <Select labelId="user-type-label" name="userType" value={formData.userType} onChange={handleChange} label="User Type" endAdornment={<Tooltip title="Select user's operational role (Maker creates, Checker reviews/approves)." placement="top" arrow data-id="element-1331">
                  <IconButton size="small" sx={{
              mr: 0.5,
              color: 'rgba(0, 0, 0, 0.54)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }} onClick={e => e.stopPropagation()} data-id="element-1332">
                    <InfoIcon size={16} data-id="element-1333" />
                  </IconButton>
                </Tooltip>} data-id="element-1330">
              <MenuItem value="maker" data-id="element-1334">Maker</MenuItem>
              <MenuItem value="checker" data-id="element-1335">Checker</MenuItem>
            </Select>
            <FormHelperText data-id="element-1336">
              {errors.userType || "Select user's operational role (Maker creates, Checker reviews/approves)."}
            </FormHelperText>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-1337">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-1338">
          <Button variant="outlined" onClick={onClose} data-id="element-1339">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-1340">
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default PartnerUserFormDrawer;